import { Component, OnInit, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { finalize } from "rxjs/operators";
import { LessonService } from "src/app/core/http/lesson.service";
import { ImportFileResult } from "src/app/shared/models";
import { ToastService } from "src/app/core/services/toast.service";
import { CommonModule } from "@angular/common";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatChipsModule } from "@angular/material/chips";
import { MatIconModule } from "@angular/material/icon";

@Component({
  selector: "import-lesson-modal",
  templateUrl: "import-lesson-modal.component.html",
  styleUrls: ["import-lesson-modal.component.scss"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatIconModule,
  ],
})
export class ImportLessonModalComponent implements OnInit {
  public fileName: string;
  public uploadInProgress: boolean = false;
  public showImage: boolean = false;
  public file: File;
  public importForm = new FormGroup({
    fileNickname: new FormControl("", Validators.required),
    fileDescription: new FormControl("", Validators.required),
  });
  public showDisclaimer: boolean = true;
  public count: any;
  public importCompleted: boolean = false;
  public selectedTemplateType: string = "";
  importFileResult: ImportFileResult = new ImportFileResult();
  bulkImportWikiUrl =
    "https://dev.azure.com/chevron/ETC-FE-EnterpriseLessonsLearned/_wiki/wikis/ETC-FE-EnterpriseLessonsLearned.wiki/122996/Bulk-Importing-Lessons";
  response = null;
  public nickname: string =
    'A nickname that will be used (by others) through the bulk workflow to identify the learnings.For example: "Chevron Shipping Sharepoint".';
  public sourceDescription: string =
    "A detailed description which identifies the origins of the learnings associated with this bulk import for traceability purposes. For example: ‘Imported from legacy Chevron Shipping Company (CSC) Lessons Learned Database in 2021. This latter database of learnings consisted of vetted learnings compiled by CSC employees seconded to Chevron MCPs as well as learnings captured from CSC tanker vessels.";
  public coreReferences: any;
  isPLB: boolean = false;
  showInfo: boolean = false;
  public fileMissingError: boolean = false;

  get fileNickname(): string {
    return this.importForm.value.fileNickname;
  }

  get fileDescription(): string {
    return this.importForm.value.fileDescription;
  }

  constructor(
    public dialogRef: MatDialogRef<ImportLessonModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private lessonService: LessonService,
    private toastService: ToastService
  ) {}

  public ngOnInit() {
    let referenceConfigCache = JSON.parse(
      localStorage.getItem("referenceConfig")
    );
    this.coreReferences = referenceConfigCache.core;
    this.coreReferences.DownloadTemplate.sort((a: any, b: any) => {
      return a.Description.localeCompare(b.Description);
    });

    if (this.data?.workflow) {
      this.selectedTemplateType = "BulkImport_" + this.data.workflow;
      this.isPLB = true;
    }
  }

  onTemplateSelected($event) {
    // this.showInfo = true;
  }

  public onFileSelected(event: any) {
    this.file = event.target.files[0];
    if (this.file) {
      this.fileName = this.file.name;
      this.fileMissingError = false;
    }
  }

  public downloadTemplate() {
    this.lessonService
      .downloadTemplate(this.selectedTemplateType)
      .subscribe((res: Response) => {
        const file: any = res.body;
        let link = document.createElement("a");
        link.download = this.selectedTemplateType.toString() + ".xlsm";
        link.href = URL.createObjectURL(file);
        link.click();
        URL.revokeObjectURL(link.href);
      });
    this.showInfo = true;
  }

  public downloadTemplateV2() {
    this.lessonService
      .downloadTemplateV2("standardFEWorkflow")
      .subscribe((res: Response) => {
        const file: any = res.body;
        let link = document.createElement("a");
        link.download = "standardFEWorkflow" + ".xlsx";
        link.href = URL.createObjectURL(file);
        link.click();
        URL.revokeObjectURL(link.href);
      });
  }

  public upload() {
    this.uploadInProgress = true;
    this.importCompleted = false;
    if (this.file) {
      const formData = new FormData();
      formData.append("file", this.file);
      if (this.data?.workflow) {
        const upload$ = this.lessonService.uploadPlbLesson(
          formData,
          this.fileNickname,
          this.fileDescription,
          this.data.projectCode,
          this.data.businessUnit,
          this.data.workflow
        );
        upload$
          .pipe(finalize(() => (this.uploadInProgress = false)))
          .subscribe((response) => {
            this.importCompleted = true;
            this.showDisclaimer = false;
            this.toastService.showSuccess(
              true,
              "Successfully uploaded lessons! Please navigate to your actions accordingly."
            );
            setTimeout(() => {
              this.dialogRef.close(response);
            }, 5000);
          });
      } else {
        const upload$ = this.lessonService.uploadLesson(
          formData,
          this.fileNickname,
          this.fileDescription
        );
        upload$
          .pipe(finalize(() => (this.uploadInProgress = false)))
          .subscribe((response) => {
            this.importCompleted = true;
            this.showDisclaimer = false;
            this.response = response;
            this.toastService.showSuccess(
              true,
              "Successfully uploaded lessons! Please navigate to your actions accordingly."
            );
            setTimeout(() => {
              this.dialogRef.close(response);
            }, 2000);
          });
      }
    }
    // if(this.uploadInProgress = false){
    //   this.toastService.showSuccess(true, 'Successfully uploaded lessons! Please navigate to your actions accordingly.');
    // }
  }

  public uploadV2() {
    this.fileMissingError = !this.file;
    this.importForm.markAllAsTouched();

    if (this.fileMissingError || this.importForm.invalid) {
      return;
    }

    this.uploadInProgress = true;
    this.importCompleted = false;
    if (this.file) {
      const formData = new FormData();
      formData.append("file", this.file);
      if (this.data?.workflow) {
        const upload$ = this.lessonService.uploadPlbLesson(
          formData,
          this.fileNickname,
          this.fileDescription,
          this.data.projectCode,
          this.data.businessUnit,
          this.data.workflow
        );
        upload$
          .pipe(finalize(() => (this.uploadInProgress = false)))
          .subscribe((response) => {
            this.importCompleted = true;
            this.showDisclaimer = false;
            this.toastService.showSuccess(
              true,
              "Successfully uploaded lessons! Please navigate to your actions accordingly."
            );
            setTimeout(() => {
              this.dialogRef.close(response);
            }, 5000);
          });
      } else {
        const upload$ = this.lessonService.uploadLessonV2(
          formData,
          this.fileNickname,
          this.fileDescription
        );
        upload$
          .pipe(finalize(() => (this.uploadInProgress = false)))
          .subscribe((response) => {
            this.importCompleted = true;
            this.showDisclaimer = false;
            this.response = response;
            this.toastService.showSuccess(
              true,
              "Successfully uploaded lessons! Please navigate to your actions accordingly."
            );
            setTimeout(() => {
              this.dialogRef.close(response);
            }, 2000);
          });
      }
    }
    // if(this.uploadInProgress = false){
    //   this.toastService.showSuccess(true, 'Successfully uploaded lessons! Please navigate to your actions accordingly.');
    // }
  }

  public removeFile() {
    this.file = null;
    this.fileName = null;
  }
}
